'use client';

import type {
  ComponentBaseProps,
  FormConfigProps,
} from '@sola/schema/lib/schema.ts';
import {
  FormDynamicSettings,
  FormDynamicVariantEnum,
} from '@sola/schema/lib/schema.ts';
import {
  submitLeadChurchForm,
  submitLeadEventForm,
  submitSubscription,
} from '@sola/skippy-sdk/requests.ts';

import { HeaderGroup } from '../../../molecules/header-group/header-group.tsx';
import { EventForm } from '../../../organisms/forms/event-form/event-form.tsx';
import { FormEmailSubscribe } from '../../../organisms/forms/form-email-subscribe/form-email-subscribe.tsx';
import { LeadForm } from '../../../organisms/forms/lead-form/lead-form.tsx';

export type FormDynamicCoreProps = FormConfigProps & ComponentBaseProps;

const formVariantMap: Record<
  FormDynamicVariantEnum,
  (s: FormDynamicSettings) => React.ReactNode
> = {
  [FormDynamicVariantEnum.enum.subscription]: (s) => (
    <FormEmailSubscribe
      subscriptionCode={s.subscriptionCode || 'Leads_General_Sign_Up'}
      onSubmit={submitSubscription}
      config={s}
      isFirstNameRequired
      isLastNameRequired
    />
  ),
  [FormDynamicVariantEnum.enum.lead]: (s) => {
    const subscriptionCode = s.subscriptionCode || 'Leads_General_Sign_Up';
    const hasShipping = s.hasChurch === false && s.hasChurch !== undefined;
    return (
      <LeadForm
        onSubmit={submitLeadChurchForm(
          s.leadType || 'contactme',
          subscriptionCode,
        )}
        config={{
          ...s,
          hasShipping,
        }}
      />
    );
  },
  [FormDynamicVariantEnum.enum.event]: (s) => (
    <EventForm
      onSubmit={submitLeadEventForm(s.leadType || 'registration')}
      config={s}
    />
  ),
};

export const FormDynamicCore = ({
  seoLevel,
  settings,
  config,
  ...props
}: FormDynamicCoreProps) => {
  const source = config ?? settings ?? {};
  const formDynamicSettings = FormDynamicSettings.parse(source);

  const formType =
    formDynamicSettings.formType ?? FormDynamicVariantEnum.enum.subscription;

  const hasHeaderContent =
    !!props.title ||
    !!props.subtitle ||
    (Array.isArray(props.body) && props.body.length > 0);

  const renderForm = formVariantMap[formType];

  return (
    <section className="component component--no-header">
      <div className="container flex flex-col gap-roomy-xs lg:gap-roomy-sm">
        {hasHeaderContent && <HeaderGroup {...props} seoLevel={seoLevel} />}
        {formDynamicSettings && renderForm && renderForm(formDynamicSettings)}
      </div>
    </section>
  );
};
